import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { ShellSpinner } from '@/components/ShellSpinner';

/**
 * Route guard for anything behind a sign-in. Replaces the `RequiresAuthentication`
 * redirect middleware that used to answer document requests with a 302 — Phase 0
 * reduced that middleware to returning 401 for `/api`, and this is the
 * client-side half of the same rule.
 */
export function RequireAuth() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Redirecting while /me is still in flight would bounce every authenticated
  // user off a deep link on a cold load.
  if (isLoading) return <ShellSpinner />;

  // The paths are the Razor `@page` routes, unchanged: the middleware still
  // redirects document requests to /auth-login and /auth-changepassword, and
  // existing bookmarks and emailed links point at them.
  if (!isAuthenticated) {
    // `state.from` lets the login screen return the user where they were headed.
    return <Navigate to="/auth-login" replace state={{ from: location }} />;
  }

  // The other middleware Phase 0 rewrote: an expired password blocks everything
  // except the change-password screen itself.
  if (user?.mustChangePassword && location.pathname !== '/auth-changepassword') {
    return <Navigate to="/auth-changepassword" replace />;
  }

  // A role that mandates MFA blocks everything until the user enrols. The order
  // matters and must match the server: the password gate above runs first in
  // ApplicationBuilderExtensions too, so when both apply the user is sent to set
  // a password before setting up a second factor. This is only the client half —
  // the middleware refuses the API calls regardless of what the SPA renders.
  if (user?.mustSetupMfa && location.pathname !== '/auth-mfa-setup') {
    return <Navigate to="/auth-mfa-setup" replace />;
  }

  return <Outlet />;
}
